import CancelablePromise from 'cancelable-promise';
import { getServiceUrl } from './api.config';
import {
  OrganizationRecipient,
  OrganizationRelation,
  PermissionConfiguration,
  PermissionGroupEdit,
  ResponseWithBody,
  ResponseWithData,
  User,
  UserClientCredential,
  UserInvitation,
} from './types';
import { ApiClientOptions } from './types/api';
import ApiClient from './utils/ApiClient';
import checkNot from './utils/checkNot';
import qs from './utils/query';
import {
  isArray,
  isBoolean,
  isNotEmptyArray,
  isNotEmptyString,
  isPositiveInteger,
  isTrue,
} from './utils/validators';

interface Group {
  id: number;
  name: string;
}

export default class AuthApi extends ApiClient {
  public constructor(options: ApiClientOptions = {}) {
    super(getServiceUrl('auth'), options);
    this.defaultTargetMarketId = options.defaultTargetMarketId || 250;
  }

  public OrganizationShow(
    id: number | string,
    withs: any = {},
    useCache: boolean = true,
  ) {
    const query = `/auth/v2/organization/${id}`;
    const params: any = {};

    if (isTrue(withs.glnData)) {
      params.with_gln_data = 1;
    }
    if (isTrue(withs.status)) {
      params.with_status = 1;
    }
    if (isTrue(withs.network)) {
      params.with_network = 1;
    }
    if (isTrue(withs.group)) {
      params.with_group = 1;
    }
    if (isTrue(withs.extraInfo)) {
      params.with_extra_info = 1;
    }
    if (isTrue(withs.domains)) {
      params.with_domains = 1;
    }

    return this.get(query, qs.stringify(params, true), useCache);
  }

  public OrganizationConfiguration(id: number | string | null = null) {
    if (id) {
      const query = `/auth/v3/permissions/configuration`;
      return this.get(query, qs.stringify({ organization_id: id }, true));
    } else {
      return this.get(`/auth/v3/permissions/configuration/all`);
    }
  }

  public OrganizationConfigurationUpdate(
    id: number | string,
    configuration: Partial<PermissionConfiguration>,
  ) {
    const _configuration = {
      dimensions: Object.entries(configuration?.scopes || {})
        .map(([entityType, dimensions = []]) => ({
          [entityType]: dimensions.map(({ dimension_key }) => dimension_key),
        }))
        .reduce(
          (acc, entityDimensions) => ({
            ...acc,
            ...entityDimensions,
          }),
          {},
        ),
      permissions: Object.entries(configuration?.roles || {})
        .map(([entityType, permissions = []]) => ({
          [entityType]: permissions.map(({ permission_key }) => permission_key),
        }))
        .reduce(
          (acc, entityPermissions) => ({
            ...acc,
            ...entityPermissions,
          }),
          {},
        ),
    };
    const query = `/auth/v3/permissions/configuration/${id}`;
    return this.put(query, { data: _configuration });
  }

  public OrganizationUpdate(entity: any) {
    const query = `/auth/v2/organization/${entity.id}`;
    return this.put(query, entity);
  }

  public OrganizationDataShow(id: number | string, useCache = true) {
    const query = `/auth/v2/organization/${id}/data`;
    return this.get(query, '', useCache);
  }

  public OrganizationDataUpdate(id: number | string, data: any) {
    const query = `/auth/v2/organization/${id}/data`;
    return this.put(query, data);
  }

  public OrganizationCreate(entity: any) {
    const query = '/auth/v2/organization';
    return this.post(query, entity);
  }

  public OrganizationUsers(id: number | string) {
    const query = `/auth/v1/organization/${id}/users`;
    return this.get(query, '');
  }

  public OrganizationUsersV2(id: number | string, queries: string) {
    const query = `/auth/v2/organization/${id}/users${queries}`;
    return this.get(query, '');
  }

  public OrganizationUserList({ queries = {}, cache }: any = {}) {
    const query = '/auth/v2/organization/users';
    return this.get(query, qs.stringify(queries, true), cache);
  }

  public OrganizationBrands(id: number | string) {
    const query = `/auth/v1/organization/${id}/brands`;
    return this.get(query, '');
  }

  public OrganizationBrandUnclaim(id: number | string) {
    const query = `/auth/v2/organization/brands/${id}`;
    return this.delete(query);
  }

  public OrganizationList(
    queries: any = {},
    filters: any = {},
    sort: any = {},
    offset: number,
    limit: number,
  ) {
    const query = '/auth/v2/organization';
    const params: any = {};

    if (checkNot(filters.network_id, ['undefined', 'null', 'empty'])) {
      params.filter_network_id = filters.network_id;
    }
    if (checkNot(filters.like_namelegal, ['undefined', 'null', 'empty'])) {
      params.like_namelegal = filters.like_namelegal;
    }
    if (checkNot(filters.id, ['undefined', 'null', 'empty'])) {
      params.filter_id_in = filters.id;
    }
    if (
      checkNot(filters.organization_type_in, ['undefined', 'null', 'empty'])
    ) {
      params.organization_type_in = filters.organization_type_in;
    }
    if (checkNot(filters.organization_type, ['undefined', 'null', 'empty'])) {
      params.organization_type = filters.organization_type;
    }
    if (checkNot(filters.target_market_ids, ['undefined', 'null', 'empty'])) {
      params.filter_target_market_ids = filters.target_market_ids;
    }
    if (checkNot(filters.exclude_parents, ['undefined', 'null', 'empty'])) {
      params.exclude_parents = filters.exclude_parents;
    }
    if (checkNot(filters.with_prospects, ['undefined', 'null', 'empty'])) {
      params.with_prospects = filters.with_prospects;
    }
    if (checkNot(queries.with_relations, ['undefined', 'null', 'empty'])) {
      params.with_relations = queries.with_relations;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      params.limit = limit;
    }
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      params.offset = offset;
    }

    if (sort.by) {
      params.sort_by = sort.by;
    }
    if (sort.order) {
      params.sort_order = sort.order;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public OrganizationGuess(username: string, filters: any = {}) {
    const query = '/auth/v1/user/organizationguess';
    const params: any = {
      username,
    };

    if (isArray(filters.targetMarkets)) {
      params.target_market_in = filters.targetMarkets;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public OrganizationOwnsGlnList(filters: any = {}) {
    const query = '/auth/v2/organizationownsgln';
    let parameters = '?alk=1';

    if (isNotEmptyString(filters.filterGln)) {
      parameters += `&filter_gln=${filters.filterGln}`;
    }
    if (isArray(filters.filterGlnIn)) {
      parameters += `&filter_gln_in=${filters.filterGlnIn.join()}`;
    }
    return this.get(query, parameters);
  }

  public OrganizationOwnsGlnUpdate(ooGln: any) {
    const query = `/auth/v2/organizationownsgln/${ooGln.id}`;
    return this.put(query, ooGln);
  }

  public OrganizationOwnsGlnDelete(ooglnId: any) {
    const query = `/auth/v2/organizationownsgln/${ooglnId}`;
    return this.delete(query, {});
  }

  public OrganizationListV3(
    filters: any = {},
    withs: any = {},
    sort: any = {},
    offset?: number,
    limit?: number,
    {
      useCache = true,
      method = 'get',
    }: {
      useCache?: boolean;
      method?: 'get' | 'post';
    } = {},
  ) {
    // This route is for admin only
    const query = '/auth/v3/organization';
    const params: any = {};

    // Filters
    if (isNotEmptyString(filters.nameLegal)) {
      params.like_namelegal = filters.nameLegal;
    }
    if (isArray(filters.ids)) {
      params.filter_id_in = filters.ids;
    }
    if (isArray(filters.types)) {
      params.type_in = filters.types;
    }
    if (isArray(filters.targetMarketIds)) {
      params.target_market_id_in = filters.targetMarketIds;
    }
    if (isArray(filters.networks)) {
      params.network_in = filters.networks;
    }
    if (isArray(filters.groups)) {
      params.group_in = filters.groups;
    }
    if (isArray(filters.statuses)) {
      params.status_in = filters.statuses;
    }

    // Withs
    if (isTrue(withs.localization)) {
      params.with_localization = 1;
    }
    if (isTrue(withs.fieldsUsed)) {
      params.with_fields_used = 1;
    }
    if (isTrue(withs.network)) {
      params.with_network = 1;
    }
    if (isTrue(withs.group)) {
      params.with_group = 1;
    }
    if (isTrue(withs.users)) {
      params.with_users = 1;
    }
    if (isTrue(withs.brands)) {
      params.with_brands = 1;
    }
    if (isTrue(withs.productsegments)) {
      params.with_productsegments = 1;
    }
    if (isTrue(withs.preferences)) {
      params.with_preferences = 1;
    }
    if (isTrue(withs.settings)) {
      params.with_settings = 1;
    }
    if (isTrue(withs.type)) {
      params.with_type = 1;
    }
    if (isTrue(withs.status)) {
      params.with_status = 1;
    }
    if (isTrue(withs.lastLoggedIn)) {
      params.with_last_logged_in = 1;
    }
    if (isTrue(withs.stats)) {
      params.with_stats = 1;
    }
    if (isBoolean(withs.totalResults)) {
      params.with_total_results = withs.totalResults ? 1 : 0;
    }

    // Sort
    if (isNotEmptyString(sort.by)) {
      params.order_by = sort.by;
    }
    if (isBoolean(sort.asc)) {
      params.order_asc = sort.asc ? 1 : 0;
    }

    // Limit/Offset
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      params.offset = offset;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      params.limit = limit;
    }

    switch (method) {
      case 'post':
        return this.post(query, params);
      default:
        return this.get(query, qs.stringify(params, true), useCache);
    }
  }

  public OrganizationProfile(
    organizationUuid: string | number,
    queries: any = {},
    useCache: boolean = true,
  ) {
    const query = `/auth/v3/organization/${organizationUuid}/profile`;
    return this.get(query, qs.stringify(queries, true), useCache);
  }

  // ===========================================================================
  //                                 News APIs
  // ===========================================================================

  public NewsList() {
    const query = '/auth/v3/users/news';
    return this.get(query);
  }

  // ===========================================================================
  //                               Tutorial APIs
  // ===========================================================================

  public TutorialList(notSeenOnly: boolean) {
    const query = '/auth/v3/users/tutorials';
    const params: any = {};

    if (notSeenOnly) {
      params.only_not_seen = 1;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public TutorialDeactivate(tutorialId: number | string) {
    const query = `/auth/v3/users/tutorials/${tutorialId}/dismiss`;
    return this.put(query, {});
  }

  // ===========================================================================
  //                               CGU APIs
  // ===========================================================================

  public CGUShow(params: any) {
    const query = '/auth/v2/cgu';
    return this.get(query, qs.stringify(params, true));
  }

  public OrganizationAcceptCGU(organizationId: number, cguId?: number) {
    const query = `/auth/v2/cgu/organization/${organizationId}`;
    const parameters: any = {};

    if (checkNot(cguId, ['undefined', 'null', 'empty'])) {
      parameters.cgu_id = cguId;
    }
    return this.post(query, parameters);
  }

  // ===========================================================================
  //                                   PLUGIN APIs
  // ===========================================================================

  public PluginAdd(memberId: number, config?: any) {
    const query = `/auth/v1/user/${memberId}/plugin`;
    return this.post(query, config);
  }

  public PluginRemove(memberId: number, config?: any) {
    const query = `/auth/v1/user/${memberId}/plugin`;
    return this.delete(query, config);
  }

  // ===========================================================================
  //                                   USER APIs
  // ===========================================================================

  public UserStats() {
    const query = '/auth/v2/user/stats';
    return this.get(query);
  }

  public UserActivationCheck(username: string, token: string) {
    const query = '/auth/v2/user/activate/check';
    return this.get(query, qs.stringify({ username, token }, true));
  }

  public UserActivate(username: string, token: string) {
    const query = '/auth/v2/user/activate';
    return this.get(query, qs.stringify({ username, token }, true));
  }

  public UserMe(
    queries: {
      withOrganizationSettings?: boolean;
      withUserSettings?: boolean;
      withNetwork?: boolean;
      withSsoInfo?: boolean;
      withOrganizationLegalIdentifiers?: boolean;
      withModules?: boolean;
      withOrganizationList?: boolean;
    } = {},
    useCache: boolean = true,
  ) {
    const query = '/auth/v2/user/me';
    const params: {
      refresh_notifications: 1;
      with_organization_settings?: 1;
      with_user_settings?: 1;
      with_network?: 1;
      with_sso_info?: 1;
      with_organization_legalidentifiers?: 1;
      with_modules?: 1;
      with_organizations_list?: true;
    } = {
      refresh_notifications: 1,
    };
    if (queries.withOrganizationSettings) {
      params.with_organization_settings = 1;
    }
    if (queries.withUserSettings) {
      params.with_user_settings = 1;
    }
    if (queries.withNetwork) {
      params.with_network = 1;
    }
    if (queries.withSsoInfo) {
      params.with_sso_info = 1;
    }
    if (queries.withOrganizationLegalIdentifiers) {
      params.with_organization_legalidentifiers = 1;
    }
    if (queries.withModules) {
      params.with_modules = 1;
    }
    if (queries.withOrganizationList) {
      params.with_organizations_list = true;
    }

    return this.get<User>(query, qs.stringify(params, true), useCache);
  }

  public UserShow(id: number, queries: any = {}, useCache: boolean = true) {
    const query = `/auth/v2/user/${id}`;
    return this.get(query, qs.stringify(queries, true), useCache);
  }

  public UserList(
    queries: any = {},
    filters: any = {},
    offset?: number,
    limit?: number,
  ) {
    const query = '/auth/v2/user';
    const params: any = {};
    if (checkNot(queries.username, ['undefined', 'null', 'empty'])) {
      params.like_username = queries.username;
    }

    if (checkNot(filters.managesBrand_id, ['undefined', 'null', 'empty'])) {
      params.filter_managesbrand_id = filters.managesBrand_id;
    }

    if (isNotEmptyArray(filters.ids)) {
      params.filter_id_in = filters.ids.join();
    }
    if (isPositiveInteger(filters.ids) || isNotEmptyString(filters.ids)) {
      params.filter_id_in = filters.ids;
    }

    if (isNotEmptyArray(filters.belongsTo_id)) {
      params.filter_belongsto_id = filters.belongsTo_id.join();
    }
    // backward compatibility
    if (
      isPositiveInteger(filters.belongsTo_id) ||
      isNotEmptyString(filters.belongsTo_id)
    ) {
      params.filter_belongsto_id = filters.belongsTo_id;
    }

    if (isTrue(filters.admin)) {
      params.filter_admin = 1;
    }

    if (filters.status != null) {
      params.filter_status = filters.status;
    }
    if (filters.platform != null) {
      params.filter_platform = filters.platform;
    }

    if (checkNot(filters.withPermissions, ['undefined', 'null'])) {
      params.with_permissions = filters.withPermissions ? '1' : '0';
    }
    if (checkNot(filters.withType, ['undefined', 'null'])) {
      params.with_type = filters.withType ? '1' : '0';
    }
    if (checkNot(filters.withPendingOrganizations, ['undefined', 'null'])) {
      params.with_pending_organizations = filters.withPendingOrganizations
        ? '1'
        : '0';
    }
    if (isTrue(filters.withTotalResults)) {
      params.with_total_results = 1;
    }

    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      params.offset = offset;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      params.limit = limit;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public UserListByPermissions(reference: string) {
    const query = '/auth/v3/users/search/allowedfor';
    const params: any = {};
    if (isNotEmptyString(reference)) {
      params.gtin = reference;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public UserCreate(user: any) {
    const query = '/auth/v1/user';
    return this.post(query, user);
  }

  public UserUpdate(user: any) {
    const query = `/auth/v2/user/${user.id}`;
    return this.put(query, user);
  }

  public UserListTokens(userId: number) {
    const query = `/auth/v2/token/${userId}`;
    return this.get(query);
  }

  public UserExpireTokens(userId: number) {
    const query = `/auth/v2/token/${userId}`;
    return this.delete(query);
  }

  public UserMove(user: any, organization: any, role?: string) {
    const query = `/auth/v2/user/${user.id}/move`;
    const parameters: {
      destination_id: number;
      role?: string;
    } = {
      destination_id: organization.id,
    };
    if (role) {
      parameters.role = role;
    }

    return this.put(query, parameters);
  }

  public UserReactivate(user: any) {
    const query = `/auth/v2/user/${user.id}/reactivate`;
    return this.put(query, {});
  }

  public UserDeactivate(user: any) {
    const query = `/auth/v2/user/${user.id}/deactivate`;
    return this.put(query, {});
  }

  public UserInvite(invitation: UserInvitation) {
    const query = '/auth/v2/user/invite';
    return this.post(query, invitation);
  }

  public UserSignUp(user: any) {
    const query = '/auth/v1/user/signup';
    return this.post(query, user);
  }

  public UserSignUpInvitation(member: any) {
    const query = '/auth/v1/user/signup/invitation';
    return this.post(query, member);
  }

  public UserSignUpConfirmationMail(username: any) {
    const query = '/auth/v1/user/signup/confirmation/mail';
    return this.post(query, { username });
  }

  public UserChangePassword(user: any) {
    const query = '/auth/v1/user/password';
    return this.post(query, user);
  }

  public UserResetPassword(user: any) {
    const query = '/auth/v1/user/password/reset';
    return this.post(query, user);
  }

  public UserValidatePassword(user: any) {
    const query = '/auth/v2/user/password/validate';
    return this.post(query, user);
  }

  public UserValidatePasswordPublic(user: any) {
    const query = '/auth/v2/user/password/validate/public';
    return this.post(query, user);
  }

  public async UserValidateEmail(email: string) {
    const query = '/auth/v2/user/email/validate';
    const response: ResponseWithData<{
      valid: boolean;
      message?: string;
    }> = await this.post(query, { email });
    return response.data.data;
  }

  public UserForgotten(username: string, platform: string | number) {
    const query = '/auth/v1/user/password/forgotten';
    const parameters = { username, platform };
    return this.post(query, parameters);
  }

  public UserLogin(
    username: string,
    password: string,
    whitelabel: boolean = false,
  ) {
    const query = '/auth/v2/token';
    const parameters = {
      grant_type: 'password',
      password,
      platform: whitelabel ? 1 : 0,
      username,
    };
    return this.post(query, parameters);
  }

  public UserLogout() {
    const query = '/auth/v2/user/logout';
    return this.post(query, {});
  }

  public UserSettingGet({ key, userId }: any) {
    const query = `/auth/v3/user/setting/${key}`;
    const params: any = {};
    if (isPositiveInteger(userId)) {
      params.user_id = userId;
    }
    return this.get(query, qs.stringify(params, true));
  }

  public UserSettingUpsert({ key, value, userId }: any) {
    const query = `/auth/v3/user/setting/${key}`;
    const payload: any = {
      value,
    };
    if (isPositiveInteger(userId)) {
      payload.user_id = userId;
    }
    return this.put(query, payload);
  }

  // ===========================================================================
  //                               Roles APIs
  // ===========================================================================

  public UserUpsertUserWithRole = (
    userId: number,
    organization_id: number,
    role: string,
  ) => this.post(`/auth/v2/user/role/${userId}`, { organization_id, role });

  public UserAcceptInOrganization(userId: number, organizationId: number) {
    const query = `/auth/v2/user/accept/${userId}`;
    const payload = {
      organization_id: organizationId,
    };
    return this.put(query, payload);
  }

  public UserRefuseInOrganization(userId: number, organizationId: number) {
    const query = `/auth/v2/user/refuse/${userId}`;
    const payload = {
      organization_id: organizationId,
    };
    return this.put(query, payload);
  }

  // ===========================================================================
  //                        ProductVersion Permissions
  // ===========================================================================

  public ListProductSegmentsPermissions(userId: number) {
    if (!userId) {
      const error = {
        message: 'Missing parameter: userId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/auth/v3/user/${userId}/permissions/productversions`;
    return this.get(query);
  }

  public UpdateProductSegmentsPermissions(
    userId?: number,
    productsegments?: number[],
    targetMarketIds?: number[],
    newFormat?: boolean,
  ) {
    const data: any = {};

    if (isPositiveInteger(userId)) {
      data.user_id = userId;
    }

    if (isArray(productsegments)) {
      data.productsegments = productsegments;
    }

    if (isNotEmptyArray(targetMarketIds)) {
      data.target_market_ids = targetMarketIds;
    }

    if (isBoolean(newFormat)) {
      data.new_format = newFormat;
    }

    const query = '/auth/v3/user/permissions/productversions';
    return this.put(query, data);
  }

  /* Retrieve permission for the given product version */
  public UserManagesProductVersionShow(productVersionId: number) {
    const query = `/auth/v2/entity/productversion/${productVersionId}`;
    return this.post(query);
  }

  // ===========================================================================
  //                       Target Markets Permissions
  // ===========================================================================

  public ListTargetMarketsPermissions(userId: number) {
    if (!userId) {
      const error = {
        message: 'Missing parameter: userId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/auth/v3/user/${userId}/permissions/targetmarkets`;
    return this.get(query);
  }

  public UpdateTargetMarketsPermissions(
    userId?: number,
    targetMarketIds?: number[],
  ) {
    const data: any = {};

    if (isPositiveInteger(userId)) {
      data.user_id = userId;
    }

    if (isNotEmptyArray(targetMarketIds)) {
      data.target_market_ids = targetMarketIds;
    }

    const query = '/auth/v3/user/permissions/targetmarkets';
    return this.put(query, data);
  }

  // ===========================================================================
  //                                 Claims
  // ===========================================================================

  public UserClaimGlnUpdate(id: string | number, payload: any) {
    return this.put(`/auth/v1/claims/gln/${id}`, payload);
  }

  public UserClaimGlnDelete(id: string | number) {
    return this.delete(`/auth/v1/claims/gln/${id}`, {});
  }

  public UserClaimGlnCreate(payload: any) {
    return this.post('/auth/v1/claims/gln', payload);
  }

  public UserClaimGlnList(
    queries: any = {},
    filters: any = {},
    offset?: number,
    limit?: number,
    useCache: boolean = true,
  ) {
    const query = '/auth/v1/claims/gln';
    const params: any = {};

    if (isTrue(queries.withTotalResults)) {
      params.with_total_results = true;
    }
    if (checkNot(filters.gln, ['undefined', 'null', 'empty'])) {
      params.filter_gln = filters.gln;
    }
    if (checkNot(filters.username, ['undefined', 'null', 'empty'])) {
      params.filter_username = filters.username;
    }
    if (checkNot(filters.status, ['undefined', 'null', 'empty'])) {
      params.filter_status = filters.status;
    }
    if (checkNot(filters.organization_id, ['undefined', 'null', 'empty'])) {
      params.filter_organization_id = filters.organization_id;
    }
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      params.offset = offset;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      params.limit = limit;
    }

    return this.get(query, qs.stringify(params, true), useCache);
  }

  public UserClaimProductBrandCreate(
    brandName: string,
    status: string | number,
    brandId: number,
    claimAs: boolean,
  ) {
    // Deprecated, use `claimBrand`.
    return this.post('/auth/v1/claims/productbrand', {
      brand_id: brandId,
      brand_name: brandName,
      claim_as: claimAs,
      request_type: status,
    });
  }

  public claimBrand(payload: any) {
    return this.post('/auth/v1/claims/productbrand', payload);
  }

  public UserClaimProductBrandUpdate(claimId: number, status: string) {
    const query = `/auth/v1/claims/productbrand/${claimId}`;
    const payload = { status };
    return this.put(query, payload);
  }

  public UserClaimProductBrandList(
    queries: any = {},
    filters: any = {},
    offset?: number,
    limit?: number,
    useCache: boolean = true,
  ) {
    const query = '/auth/v1/claims/productbrand';
    const params: any = {};

    if (isTrue(queries.withTotalResults)) {
      params.with_total_results = true;
    }
    if (checkNot(filters.status, ['undefined', 'null', 'empty'])) {
      params.filter_status = filters.status;
    }
    if (checkNot(filters.type, ['undefined', 'null', 'empty'])) {
      params.filter_type = filters.type;
    }
    if (checkNot(filters.brandName, ['undefined', 'null', 'empty'])) {
      params.filter_brand_name = filters.brandName;
    }
    if (checkNot(filters.username, ['undefined', 'null', 'empty'])) {
      params.filter_username = filters.username;
    }

    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      params.offset = offset;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      params.limit = limit;
    }

    return this.get(query, qs.stringify(params, true), useCache);
  }

  public cleanReference(reference: string) {
    return reference.replace(/\s|,/g, '');
  }

  public UserClaimProductReferenceCreate(
    productName: string,
    reference: string,
    brandId: number,
    gln: string,
  ) {
    return this.post('/auth/v1/claims/productreference', {
      brand_id: brandId,
      gln,
      product_name: productName,
      product_reference: this.cleanReference(reference),
    }).then((data: any) => data);
  }

  public NotificationCreate(
    userIdIn: number[],
    userExtraMail: string[],
    payload: any = {},
    organizationIdIn: number[] = [],
  ) {
    const body = { ...payload };
    if (userIdIn.length > 0) {
      body.user_id_in = userIdIn.join();
    }
    if (userExtraMail.length > 0) {
      body.user_extra_mail = userExtraMail.join();
    }
    if (organizationIdIn.length > 0) {
      body.organization_id_in = organizationIdIn.join();
    }
    return this.post('/auth/v1/notification', body);
  }

  public NotificationCreateForUserType(userType: string, payload: any) {
    return this.post(`/auth/v1/notification/usertype/${userType}`, payload);
  }

  public ConnectAs(username: string, password: string, connectAs: string) {
    return this.post<
      any,
      Response & { data: { access_token: string; refresh_token: string } }
    >('/auth/v2/token', {
      connect_as: connectAs,
      grant_type: 'password',
      origin: 'dashboard_stream',
      password,
      username,
    });
  }

  public AllowedProductSegmentsIds({ queries = {}, useCache }: any = {}) {
    const query = '/auth/v2/user/productversion/allowed/productsegments';
    const params: any = {
      targetmarket_id: queries.targetMarketId || this.defaultTargetMarketId,
    };
    if (isPositiveInteger(queries.userId)) {
      params.user_id = queries.userId;
    }
    if (isPositiveInteger(queries.organizationId)) {
      params.organization_id = queries.organizationId;
    }
    if (isNotEmptyString(queries.permission)) {
      params.permission = queries.permission;
    }
    return this.get(query, qs.stringify(params, true), useCache);
  }

  // ===========================================================================
  //                               Recipients
  // ===========================================================================

  public RecipientsList({ filters = {}, sort = {}, withs = {} }: any = {}) {
    const query = '/auth/v2/organization/recipients';
    const params: any = {};

    if (isNotEmptyString(filters.nameLegal)) {
      params.like_namelegal = filters.nameLegal;
    }
    if (isBoolean(filters.listing)) {
      params.filter_listing = filters.listing ? '1' : '0';
    }
    if (isBoolean(filters.hasRfpV2)) {
      params.with_rfpv2 = filters.hasRfpV2 ? '1' : '0';
    }
    if (isBoolean(filters.notListing)) {
      params.filter_not_listing = filters.notListing ? '1' : '0';
    }
    if (isBoolean(filters.worksWithOrganization)) {
      params.filter_works_with_organization = filters.worksWithOrganization
        ? '1'
        : '0';
    }
    if (filters.hasGroup) {
      params.filter_has_group = 1;
    }
    if (isNotEmptyArray(filters.targetMarkets)) {
      params.filter_targetmarket_ids = filters.targetMarkets;
    }

    if (isNotEmptyArray(filters.targetOrganizationIds)) {
      params.target_organization_id_in = filters.targetOrganizationIds;
    }

    if (isBoolean(withs.worksWithOrganization)) {
      params.with_works_with_organization = withs.worksWithOrganization
        ? '1'
        : '0';
    }

    if (isBoolean(withs.withTargetMarkets)) {
      params.with_targetmarkets = withs.withTargetMarkets ? '1' : '0';
    }

    if (withs.fieldsUsed) {
      params.with_fields_used = 1;
    }

    if (withs.currencies) {
      params.with_currencies = 1;
    }

    if (withs.locales) {
      params.with_locales = 1;
    }

    if (withs.group) {
      params.with_group = 1;
    }

    if (isPositiveInteger(filters.limit)) {
      params.limit = filters.limit;
    }

    if (isNotEmptyString(sort.by)) {
      params.sort_by = sort.by;
    }
    if (isNotEmptyString(sort.order)) {
      params.sort_order = sort.order;
    }

    if (withs.isPaying) {
      params.with_is_paying_recipient = 1;
    }

    if (withs.groupRelation) {
      params.with_group_relation = 1;
    }

    if (withs.matchedSuppliers) {
      params.with_matched_suppliers = 1;
    }

    // NOTE only works for super admins for now
    if (isPositiveInteger(withs.organizationId)) {
      params.organization_id = withs.organizationId;
    }

    return this.get<OrganizationRecipient[], { data: OrganizationRecipient[] }>(
      query,
      qs.stringify(params, true),
    );
  }

  // ===========================================================================
  //                               Admin only
  // ===========================================================================

  public OrganizationFullList() {
    const query = '/auth/v2/organization/minimal';
    return this.get(query);
  }

  public NetworkList() {
    const query = '/auth/v1/networks';
    return this.get(query);
  }

  public NetworkCreate(networkName: string) {
    const query = '/auth/v1/network';
    return this.post(query, { name: networkName });
  }

  public updateOrganizationUsesFields(organization: any) {
    const query = `/auth/v3/organization/${organization.id}/fields`;
    return this.post(query, organization);
  }

  // ===========================================================================
  //                               Org settings
  // ===========================================================================

  public OrganizationSettingGet(key: string, useCache: boolean = true) {
    const query = `/auth/v2/organization/setting/${key}`;
    return this.get(query, '', useCache);
  }

  public OrganizationSettingUpsert({ organizationId, key, value }: any) {
    const query = `/auth/v2/organization/setting/${key}`;
    return this.put(query, { organization_id: organizationId, value });
  }

  // ===========================================================================
  //                        OrganizationWorksWithOrganization
  // ===========================================================================

  /**
   *
   * @param owwo, must contain target_organization_id:integer and isActive:boolean
   */
  public OrganizationWorksWithOrganizationUpsert(owwo: any) {
    return this.post('/auth/v3/organizationworkswithorganization/upsert', owwo);
  }

  public OrganizationWorksWithOrganizationDelete(
    sourceOrganizationId: number,
    targetOrganizationId: number,
  ) {
    return this.delete(`/auth/v3/organizationworkswithorganization`, {
      source_organization_id: sourceOrganizationId,
      target_organization_id: targetOrganizationId,
    });
  }

  public OrganizationWorksWithOrganizationForOrganization(
    organizationId: number,
    filters: any = {},
    withs: any = {},
    useCache: boolean = true,
  ) {
    const query = `/auth/v3/organizationworkswithorganization/organization/${organizationId}`;
    const params: any = {};

    if (isNotEmptyArray(filters.typeIn)) {
      params.filter_type_in = filters.typeIn;
    }
    if (isBoolean(filters.isActive)) {
      params.filter_isactive = filters.isActive;
    }
    if (isBoolean(withs.organizationName)) {
      params.with_organization_name = withs.organizationName;
    }
    return this.get(query, qs.stringify(params, true), useCache);
  }

  // ===========================================================================
  //                      OrganizationManaged
  // ===========================================================================

  public OrganizationManagedPermissions({ useCache = true } = {}) {
    const query = '/auth/v2/entity/organizationmanaged/permissions';
    return this.get(query, '', useCache);
  }

  public GetPermissionsForOrganizationManaged(
    organizationId: number,
    useCache: boolean = true,
  ) {
    const query = `/auth/v2/entity/organizationmanaged/${organizationId}`;
    return this.post(query, {}, useCache);
  }

  // ===========================================================================
  //                               Org group
  // ===========================================================================

  public listOrganizationGroups(search: string) {
    return this.get(
      '/auth/v3/organizationgroups',
      qs.stringify({ search }, true),
    );
  }

  public upsertOrganizationGroup(data: any) {
    return this.post('/auth/v3/organizationgroup', data);
  }

  public async fetchMyOrganizationRelations({ useCache = true } = {}) {
    const response: ResponseWithData<OrganizationRelation[]> = await this.get(
      '/auth/v3/organizationgroup/relations',
      '',
      useCache,
    );
    return response.data.data;
  }

  public async fetchOrganizationGroupRelations({
    useCache = true,
    ...params
  }: {
    useCache?: boolean;
    organization_id?: number;
  } = {}) {
    const response: ResponseWithData<OrganizationRelation[]> = await this.get(
      '/auth/v3/organizationgroup/relations',
      qs.stringify(params, true),
      useCache,
    );
    return response.data.data;
  }

  public addOrganizationGroupRelations(
    groupIds: number[],
    force?: boolean,
    organizationId?: number,
  ) {
    return this.post('/auth/v3/organizationgroup/relations/add', {
      group_ids: groupIds,
      organization_id: organizationId,
      force,
    });
  }

  public removeOrganizationGroupRelations(
    groupIds: number[],
    force?: boolean,
    organizationId?: number,
  ) {
    return this.delete('/auth/v3/organizationgroup/relations/remove', {
      group_ids: groupIds,
      organization_id: organizationId,
      force,
    });
  }

  // ===========================================================================
  //                             Client credentials
  // ===========================================================================

  public createClientCredential(name: string) {
    return this.post<UserClientCredential>('/auth/v2/user/credentials', {
      name,
    });
  }

  public deleteClientCredential(id: number) {
    return this.delete<any, any>(`/auth/v2/user/credentials/${id}`);
  }

  public listClientCredentials() {
    return this.get<[UserClientCredential]>(`/auth/v2/user/credentials`);
  }

  // ===========================================================================
  //                             Group Permissions
  // ===========================================================================

  public getGroupPermissionsForUser(user_id: number) {
    return this.get<{ all_groups: boolean; groups?: Group[] }>(
      `/auth/v3/user/${user_id}/permissions/groups`,
    ).then(this.convertGroupPermissionsResponse);
  }

  public setGroupPermissionsForUser(
    user_id: number,
    all_groups: boolean,
    groups?: Group[],
  ) {
    return this.put<{ all_groups: boolean; groups?: Group[] }>(
      '/auth/v3/user/permissions/groups',
      {
        all_groups,
        groups,
        user_id,
      },
    ).then(this.convertGroupPermissionsResponse);
  }

  private convertGroupPermissionsResponse(
    response: ResponseWithData<{ all_groups: boolean; groups?: Group[] }>,
  ) {
    const { all_groups, groups } = response.data.data;
    return {
      enabled: all_groups,
      groups,
    };
  }

  // ===========================================================================
  //                       Organization Permissions
  // ===========================================================================

  public updateOrganizationPermissions(
    userId: number,
    organizationId: number,
    permissions: any,
  ) {
    const query = `/auth/v3/user/${userId}/permissions/organization/${organizationId}`;
    const data = { permissions };
    return this.put(query, data);
  }

  /* Retrieve current users permission for the given organization */
  public userManagesOrganizationShow(organizationId: number) {
    const query = `/auth/v2/entity/organization/${organizationId}`;
    return this.post(query);
  }

  /* Retrieve given user UME for the given organization */
  public managedUserOrganizationPermissions(
    userId: number,
    organizationId: number,
  ) {
    const query = `/auth/v3/user/${userId}/permissions/organization/${organizationId}`;
    return this.get(query);
  }

  // ===========================================================================
  //                       Organization Groups Permissions
  // ===========================================================================

  public createOrganizationGroup(group: PermissionGroupEdit) {
    const query = `/auth/v3/permissions/usergroup`;
    return this.post(query, group);
  }

  public updateOrganizationGroup(group: PermissionGroupEdit) {
    const query = `/auth/v3/permissions/usergroup/${group.id}`;
    return this.put(query, group);
  }

  public fetchOrganizationGroups() {
    const query = `/auth/v3/permissions/usergroup`;
    return this.get(query);
  }

  public getOrganizationGroup(groupId: number | string) {
    const query = `/auth/v3/permissions/usergroup/${groupId}`;
    return this.get(query);
  }

  public deleteOrganizationGroup(groupId: number | string) {
    const query = `/auth/v3/permissions/usergroup/${groupId}`;
    return this.delete(query);
  }

  public upsertOrganizationGroupLinks({
    payload,
  }: {
    payload: { id: number | string; links: any[] };
  }) {
    const query = `/auth/v3/permissions/link/usergroup/${payload.id}`;
    return this.post(query, payload.links);
  }

  public getOrganizationGroupLinks(groupId: number | string) {
    const query = `/auth/v3/permissions/link/usergroup/${groupId}`;
    return this.get(query);
  }

  public upsertOrganizationGroupUser(payload: {
    user_ids?: (string | number)[];
    usernames?: string[];
    groupId: string | number;
    dryRun: boolean;
  }) {
    const query = `/auth/v3/permissions/usergroup/${payload.groupId}/users`;
    return this.put(query, {
      user_ids: payload.user_ids,
      usernames: payload.usernames,
      dry_run: payload.dryRun,
    });
  }

  public deleteOrganizationGroupUser({
    group_id,
    user_id,
  }: {
    group_id: string | number;
    user_id: string | number;
  }) {
    const query = `/auth/v3/permissions/usergroup/${group_id}/users/${user_id}`;
    return this.delete(query);
  }

  public addOrganizationGroupUser({
    group_id,
    user_id,
  }: {
    group_id: string | number;
    user_id: string | number;
  }) {
    const query = `/auth/v3/permissions/usergroup/${group_id}/users/${user_id}`;
    return this.put(query);
  }

  // ===========================================================================
  //                       Organization Swap
  // ===========================================================================

  public userLoginFromOrganization(orgId: string) {
    const query = '/auth/v2/token';
    const parameters = {
      grant_type: 'select_organization',
      organization_id: orgId,
    };
    return this.post(query, parameters);
  }

  public userOrganizationPick(
    username: string,
    password: string,
    logAs?: string,
  ): CancelablePromise<
    ResponseWithBody<{
      organization_pick_token: string;
      organizations: { id: string; last_connection_date: string }[];
    }>
  > {
    const query = '/auth/v2/token/organization_pick';
    return this.post(query, {
      username,
      password,
      ...(logAs && { connect_as: logAs }),
    });
  }

  public userLoginFromOrganizationPickToken(
    organizationId: string,
    organizationPickToken: string,
  ): CancelablePromise<
    ResponseWithBody<{ access_token: string; refresh_token: string }>
  > {
    const query = '/auth/v2/token';
    return this.post(query, {
      organization_pick_token: organizationPickToken,
      organization_id: organizationId,
      grant_type: 'organization_pick',
    });
  }
}
